/**
 * DEC-1145 / DEC-1146: client-side port of server/utils/scrubCapturedText.js.
 *
 * ── Why this is a copy, and how the copy is kept honest ──────────────────
 * Create React App refuses imports from outside client/src, so the client
 * physically cannot require the server module. The patterns are therefore
 * duplicated here — which is a drift hazard, and drift in THIS file means a
 * secret shipped over the network that everyone believed was scrubbed.
 *
 * The guard is `scrubClientText.parity.test.js`: it reads the server file and
 * asserts the two pattern sets are identical, source and all. If you edit one
 * side, that test fails until you edit the other. Do not "fix" it by relaxing
 * the comparison.
 *
 * ── This is the courtesy pass, not the control ───────────────────────────
 * Running here keeps secrets off the network hop. It is NOT what makes the
 * stored data safe — a client can send anything, so the server scrubs again
 * before insert. Never drop the server-side pass because this one exists.
 *
 * Ordering is load-bearing and was established empirically (DEC-1145 spike):
 * normalize invisible characters FIRST, then scrub. The reverse lets a
 * zero-width space split a token so no pattern matches it, after which
 * normalization strips the character and reassembles the secret in the clear.
 */

const MAX_LENGTH = 300;
const REDACTED = '[redacted]';

// eslint-disable-next-line no-control-regex -- intentional: C0/C1 control chars
const CONTROL_AND_INVISIBLE_RE = new RegExp(
  '[\\u0000-\\u0008\\u000E-\\u001F\\u007F-\\u009F\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u2064\\u2066-\\u2069\\uFEFF]',
  'g'
);

// MUST stay identical to PATTERNS in server/utils/scrubCapturedText.js.
// Enforced by scrubClientText.parity.test.js.
export const PATTERNS = [
  { name: 'query_string', re: /\?[^\s"')]*/g, to: `?${REDACTED}` },
  { name: 'url_fragment', re: /#[^\s"')]*/g, to: `#${REDACTED}` },
  { name: 'jwt', re: /eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}(?:\.[A-Za-z0-9_-]*)?/g, to: REDACTED },
  { name: 'bearer', re: /\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, to: `$1 ${REDACTED}` },
  { name: 'email', re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, to: REDACTED },
  { name: 'stripe_key', re: /\b(sk|pk|rk|whsec)_[A-Za-z0-9_]{8,}/g, to: REDACTED },
  { name: 'aws_access_key', re: /\b(AKIA|ASIA)[A-Z0-9]{12,}/g, to: REDACTED },
  {
    name: 'labelled_secret',
    re: /\b(api[_-]?key|apikey|secret|password|passwd|token|auth|credential)\b\s*[:=]\s*"?[^\s,;"'}]{4,}"?/gi,
    to: `$1=${REDACTED}`,
  },
  { name: 'long_opaque', re: /\b[A-Fa-f0-9]{32,}\b/g, to: REDACTED },
  { name: 'long_base64', re: /\b[A-Za-z0-9+/]{40,}={0,2}(?![A-Za-z0-9+/=])/g, to: REDACTED },
];

/** Strip invisible/control characters so they can't be used to split a token. */
function normalize(value) {
  return value.replace(CONTROL_AND_INVISIBLE_RE, '').trim().replace(/\s+/g, ' ');
}

/**
 * Apply every pattern to a fixed point — one pattern's replacement can expose
 * a new match for another. Bounded so pathological input can't spin.
 */
function applyPatterns(input) {
  let out = input;
  for (let i = 0; i < 5; i += 1) {
    const before = out;
    for (const { re, to } of PATTERNS) {
      out = out.replace(re, to);
    }
    if (out === before) break;
  }
  return out;
}

export function scrubClientText(value) {
  if (value === undefined || value === null) return value;
  if (typeof value !== 'string') return value;

  let out = applyPatterns(normalize(value));

  // Truncate LAST: cutting first could sever a token and leave a fragment no
  // pattern recognises — still a leak, and one that looks scrubbed.
  if (out.length > MAX_LENGTH) {
    out = `${out.slice(0, MAX_LENGTH)}…`;
  }
  return out;
}

export const LIMITS = { MAX_LENGTH, REDACTED };
export default scrubClientText;
